import React, { useState } from "react";
import { Button } from "primereact/button";
import useDashboardStore from "store/dashboard-store";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { T } from "services/lang/LanguageService";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { tabUpdateInDashboard, updateDashboardTabs } from "../../utils";

interface GroupEditModeButtonsProps {
  dashboard: Dashboard;
  tabId: number;
  group: DashboardComponentGroup;
  isComponentsReorderEnabled: boolean;
  setIsComponentsReorderEnabled: (isComponentReorderEnabled: boolean) => void;
  setGroupConfiguratorVisibility: (groupConfiguratorVisibility: boolean) => void;
  setMoveTabGroupDialogVisibility: (moveTabGroupDialogVisibility: boolean) => void;
  setGroupTitleChangeDialogVisibility: (groupTitleChangeDialogVisibility: boolean) => void;
  setDashboardComponentCreationDialogVisibility: (
    dashboardComponentCreationDialogVisibility: boolean
  ) => void;
}

const GroupEditModeButtons = ({
  dashboard,
  tabId,
  group,
  isComponentsReorderEnabled,
  setIsComponentsReorderEnabled,
  setGroupConfiguratorVisibility,
  setMoveTabGroupDialogVisibility,
  setGroupTitleChangeDialogVisibility,
  setDashboardComponentCreationDialogVisibility,
}: GroupEditModeButtonsProps) => {
  const isGroupReorderEnabled = useDashboardStore(state => state.isGroupReorderEnabled);
  const setIsGroupReorderEnabled = useDashboardStore(state => state.setIsGroupReorderEnabled);

  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);

  const handleReorderComponents = () => {
    setIsGroupReorderEnabled(false);
    setIsComponentsReorderEnabled(!isComponentsReorderEnabled);
  };

  const groupRemoveConfirm = (id: string) => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("confirm-dialog.remove-group"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-group"),
      confirmedCallback: () => {
        handleRemoveGroup(id);
      },
    });
  };

  const handleRemoveGroup = (id: string) => {
    if (!dashboard) return;

    const currentTabIndex = tabId;
    const updatedComponentGroups = dashboard.dashboardTabsJs[
      currentTabIndex
    ].componentGroups.filter((groupToFilter: DashboardComponentGroup) => groupToFilter.id !== id);

    const updatedDashboardTabs = tabUpdateInDashboard(dashboard.dashboardTabsJs, currentTabIndex, {
      componentGroups: updatedComponentGroups,
    });

    updateDashboardTabs(dashboard, updatedDashboardTabs);
  };

  return (
    <div>
      {isGroupReorderEnabled ? (
        <Button
          icon="pi pi-arrows-alt"
          className="p-button-secondary p-button-rounded p-button-text group-main-editor-button bg-white"
          disabled
        />
      ) : (
        <Button
          icon="pi pi-pencil"
          className="p-button-secondary p-button-rounded p-button-text group-main-editor-button bg-white"
          onClick={() => setIsMenuVisible(!isMenuVisible)}
        />
      )}
      {isMenuVisible && (
        <div className="menu-styles">
          <Button
            icon="pi pi-trash"
            tooltip={T("confirm-dialog.remove-group")}
            className="p-button-danger p-button-rounded"
            onClick={() => groupRemoveConfirm(group.id)}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-cog"
            tooltip={T("action.configure-group")}
            className="p-button-warning p-button-rounded"
            onClick={() => setGroupConfiguratorVisibility(true)}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-arrow-right"
            tooltip={T("action.move-group")}
            className="p-button-secondary p-button-rounded"
            onClick={() => setMoveTabGroupDialogVisibility(true)}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-arrow-right-arrow-left"
            tooltip={T("action.reorder-components")}
            className={`${isComponentsReorderEnabled ? "p-button-warning p-button-rounded rotate-90 border" : "p-button-secondary p-button-rounded rotate-90"}`}
            onClick={handleReorderComponents}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-pencil"
            tooltip={T("action.change-group-title")}
            className="p-button-success p-button-rounded"
            onClick={() => setGroupTitleChangeDialogVisibility(true)}
            tooltipOptions={{ position: "bottom" }}
          />
          <Button
            icon="pi pi-plus"
            tooltip={T("action.add-component")}
            className="p-button-success p-button-rounded"
            onClick={() => setDashboardComponentCreationDialogVisibility(true)}
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
      )}
    </div>
  );
};

export default GroupEditModeButtons;
